import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import { EcosystemLinks } from './EcosystemLinks';
import { MarketplaceIcon } from '../marketplace/MarketplaceIcon';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import HomeIcon from '@material-ui/icons/Home';
import LayersIcon from '@material-ui/icons/Layers';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { SearchModalBody } from '../search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      nav.take('page:notifications'); // Using NotificationsSidebarItem manually instead
      nav.take('page:catalog'); // Re-presented as "Catalog" (/browse) below
      nav.take('page:scaffolder'); // Re-presented as "Create" below, in reading order
      // Kept out of the rail for focus, not for safety — their routes stay
      // reachable by URL. app-visualizer is disabled outright in app-config
      // instead, so it needs no take here.
      nav.take('page:api-docs');
      nav.take('page:catalog-graph');
      nav.take('page:catalog-import'); // Reached from Create › Start instead
      nav.take('page:marketplace'); // Placed explicitly below
      // Docs (TechDocs) is off the rail at Amine's call: a component's docs are
      // reached from its own page, and the rail's lower section is for the
      // other ecosystem sites instead. The /docs route stays.
      nav.take('page:techdocs');

      return (
        // The rail is labelled: Backstage renders labels whenever the sidebar
        // is pinned, which is its default (LocalStorage `sidebarPinState`),
        // and modules/theme sizes the drawer for them. disableExpandOnHover
        // stays so the rail never animates under the pointer.
        <Sidebar disableExpandOnHover>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            {/* Our own dialog body. The stock one apologises for finding
                nothing before you have typed anything — see ../search. */}
            <SidebarSearchModal>
              {({ toggleModal, resultItemComponents }) => (
                <SearchModalBody
                  toggleModal={toggleModal}
                  resultItemComponents={resultItemComponents}
                />
              )}
            </SidebarSearchModal>
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* Order is Amine's call (2026-10-22): Home, Create, Catalog,
                Marketplace, My requests. Create and My requests used to sit at
                the foot of the rail, away from everything else — fine for an
                icon strip, odd once the items have names. */}
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            <SidebarItem
              icon={AddCircleOutlineIcon}
              to="/create/templates"
              text="Create"
            />
            <SidebarItem icon={LayersIcon} to="/browse" text="Catalog" />
            <SidebarItem
              icon={MarketplaceIcon}
              to="/marketplace"
              text="Marketplace"
            />
            <SidebarItem
              icon={PlaylistAddCheckIcon}
              to="/create/request"
              text="My requests"
            />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
            <SidebarDivider />
            <EcosystemLinks />
          </SidebarGroup>
          <SidebarSpace />
          {/*
            titleCounterEnabled defaults to true, mirroring the unread count into
            the browser tab title ("(3) Catalog"). Turned off: the hook behind it
            (useTitleCounter) installs a MutationObserver on <title> and writes to
            that tag, while react-helmet also owns it — the two rewrite each other
            and the tab text visibly flaps on navigation until they converge.

            This suppresses the count prefix only. The hook is mounted by this
            component regardless of the flag, and its effect cleanup writes the
            previous title back unguarded and unthrottled, so contention remains
            possible. Removing it outright means swapping this component for a
            plain SidebarItem, losing the unread badge and snackbar toasts.
          */}
          <NotificationsSidebarItem titleCounterEnabled={false} />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
